import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import type { User } from '@supabase/supabase-js';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabase';
import { useTourDiscounts, resolveTourDiscount, applyTourDiscount } from '@/hooks/useTourDiscounts';
import BookingForm, { type BookingResult } from './components/BookingForm';

interface TourSummary {
  id: string;
  title: string;
  price: number;
  image: string;
  duration: string;
  spots: number;
}

interface DiscountInfo {
  applied: {
    code: string;
    type: 'percent' | 'fixed';
    value: number;
    discount: number;
    discountedTotal: number;
  } | null;
  total: number;
  finalTotal: number;
}

export default function Booking() {
  const { tourId } = useParams<{ tourId: string }>();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const initialAdults = Math.max(1, parseInt(searchParams.get('adults') || '1', 10) || 1);
  const initialChildren = Math.max(0, parseInt(searchParams.get('children') || '0', 10) || 0);
  const accommodation = searchParams.get('option') || '';
  const priceParam = Number(searchParams.get('price'));
  const [tour, setTour] = useState<TourSummary | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<BookingResult | null>(null);
  const [discountInfo, setDiscountInfo] = useState<DiscountInfo>({
    applied: null,
    total: 0,
    finalTotal: 0,
  });
  const { discounts } = useTourDiscounts();
  const successRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError(null);

        const [tourResult, authResult] = await Promise.all([
          supabase
            .from('vietnamtravel_tours')
            .select('id, title, price, image, duration, spots')
            .eq('id', tourId)
            .maybeSingle(),
          supabase.auth.getUser(),
        ]);

        if (tourResult.error) throw tourResult.error;

        if (!cancelled) {
          setTour(tourResult.data as TourSummary | null);
          setUser(authResult.data.user ?? null);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load tour');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (tourId) load();
    return () => {
      cancelled = true;
    };
  }, [tourId]);

  useEffect(() => {
    if (success) {
      requestAnimationFrame(() => {
        successRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }, [success]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-100 flex flex-col">
        <Navbar />
        <main className="flex-1 w-full px-4 md:px-8 lg:px-12 pt-24 md:pt-28 pb-16">
          <div className="max-w-5xl mx-auto">
            <div className="h-8 bg-secondary-100 rounded w-1/3 animate-pulse mb-6" />
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-48 bg-secondary-50 rounded-lg animate-pulse" />
                ))}
              </div>
              <div className="h-80 bg-secondary-50 rounded-lg animate-pulse" />
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !tour) {
    return (
      <div className="min-h-screen bg-background-100 flex flex-col">
        <Navbar />
        <main className="flex-1 w-full px-4 md:px-8 lg:px-12 pt-24 md:pt-28 pb-16">
          <div className="max-w-md mx-auto text-center py-20">
            <i className="ri-error-warning-line text-5xl text-secondary-300" />
            <h1 className="mt-4 font-heading text-xl font-bold text-secondary-800">
              {error ? t('booking.loadFailed') : t('booking.notFound')}
            </h1>
            <p className="mt-2 text-sm text-secondary-500">
              {error || t('booking.removed')}
            </p>
            <Link
              to="/tours"
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line" />
              {t('booking.backToTours')}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const rawPrice = priceParam > 0 ? priceParam : tour.price;
  const priceInfo = applyTourDiscount(rawPrice, resolveTourDiscount(tour.id, discounts));
  const bookingPrice = priceInfo.finalPrice;
  const originalPrice = priceInfo.originalPrice;

  if (success) {
    return (
      <div className="min-h-screen bg-background-100 flex flex-col">
        <Navbar />
        <main className="flex-1 w-full px-4 md:px-8 lg:px-12 pt-24 md:pt-28 pb-16 flex items-center justify-center">
          <div ref={successRef} className="max-w-md mx-auto text-center py-10">
            <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
              <i className="ri-check-line text-3xl text-green-600" />
            </div>
            <h1 className="mt-6 font-heading text-2xl font-bold text-secondary-900">
              {t('booking.received')}
            </h1>
            <p className="mt-2 text-sm text-secondary-500 leading-relaxed">
              {t('booking.thanks', { title: tour.title })}
            </p>

            <p className="mt-4 text-sm text-secondary-500">
              {t('booking.checkStatus')}
            </p>

            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/profile"
                className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold text-sm px-6 py-3 rounded-full transition-colors whitespace-nowrap"
              >
                {t('booking.viewBookings')}
                <i className="ri-arrow-right-line text-sm" />
              </Link>
              <Link
                to="/tours"
                className="inline-flex items-center justify-center gap-2 border border-secondary-300 hover:border-secondary-400 text-secondary-700 font-semibold text-sm px-6 py-3 rounded-full transition-colors whitespace-nowrap"
              >
                {t('booking.exploreMore')}
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-100 flex flex-col">
      <Navbar />

      <main className="flex-1 w-full px-4 md:px-8 lg:px-12 pt-24 md:pt-28 pb-16 md:pb-20">
        <div className="max-w-5xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-secondary-500 mb-4">
            <Link to="/" className="hover:text-primary-600 transition-colors">{t('booking.home')}</Link>
            <span>/</span>
            <Link to="/tours" className="hover:text-primary-600 transition-colors">{t('booking.tours')}</Link>
            <span>/</span>
            <span className="text-secondary-700 truncate max-w-[220px]">{tour.title}</span>
          </nav>

          <h1 className="font-heading text-2xl md:text-3xl font-bold text-secondary-900 mb-2">
            {t('booking.bookJourney')}
          </h1>
          <p className="text-sm text-secondary-500 mb-8">
            {t('booking.tellUs')}
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8 items-start">
            {/* Form */}
            <div>
              {!user && (
                <div className="mb-6 flex items-start gap-3 bg-accent-100/70 border border-accent-300 rounded-lg px-4 py-3">
                  <i className="ri-information-line text-accent-900 text-base mt-0.5 shrink-0" />
                  <div className="text-sm text-accent-900">
                    <p className="font-semibold">{t('booking.noAccount')}</p>
                    <p className="text-accent-900/80">
                      {t('booking.guestNote')}{' '}
                      <Link to="/login" className="font-semibold underline">{t('booking.signIn')}</Link>{' '}
                      {t('booking.ifAlreadyHave')}
                    </p>
                  </div>
                </div>
              )}

              <BookingForm
                tour={{ id: tour.id, title: tour.title, price: bookingPrice, duration: tour.duration }}
                userEmail={user?.email || ''}
                userFullName={(user?.user_metadata?.full_name as string) || ''}
                initialAdults={initialAdults}
                initialChildren={initialChildren}
                accommodation={accommodation}
                onSuccess={(result) => setSuccess(result)}
                onDiscountChange={setDiscountInfo}
              />
            </div>

            {/* Summary card */}
            <aside className="bg-white rounded-lg border border-secondary-200 overflow-hidden sticky top-24">
              <div className="relative aspect-[3/2]">
                <img src={tour.image} alt={tour.title} className="w-full h-full object-cover object-top" />
                <div className="absolute top-3 left-3 flex items-center gap-1.5">
                  <span className="px-2.5 py-1 bg-white/90 backdrop-blur-sm rounded text-[11px] font-semibold text-secondary-700">
                    {tour.duration}
                  </span>
                  <span className="px-2.5 py-1 bg-white/90 backdrop-blur-sm rounded text-[11px] font-semibold text-secondary-700">
                    {t('booking.spots', { count: tour.spots })}
                  </span>
                </div>
              </div>
              <div className="p-5">
                <h2 className="font-heading text-base font-bold text-secondary-900 leading-snug">
                  {tour.title}
                </h2>
                <div className="mt-3 flex items-baseline gap-1.5">
                  {priceInfo.hasDiscount && (
                    <span className="text-sm font-medium text-secondary-400 line-through">
                      ${Number(originalPrice).toLocaleString()}
                    </span>
                  )}
                  <span className="font-heading text-2xl font-bold text-secondary-900">
                    ${Number(bookingPrice).toLocaleString()}
                  </span>
                  <span className="text-xs text-secondary-500">{t('booking.usdPerPerson')}</span>
                </div>

                {discountInfo.applied && (
                  <div className="mt-4 rounded-md bg-green-50 border border-green-100 p-3.5">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-secondary-500">{t('booking.subtotal')}</span>
                      <span className="text-secondary-700 font-semibold line-through">
                        ${discountInfo.total.toLocaleString()}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-sm">
                      <span className="text-green-700 font-medium flex items-center gap-1">
                        <i className="ri-coupon-3-line" />
                        {discountInfo.applied.code}
                      </span>
                      <span className="text-green-700 font-semibold">
                        −${discountInfo.applied.discount.toLocaleString()}
                      </span>
                    </div>
                    <div className="mt-2 pt-2 border-t border-green-100 flex items-center justify-between">
                      <span className="text-secondary-900 font-bold">{t('booking.total')}</span>
                      <span className="font-heading text-lg font-bold text-secondary-900">
                        ${discountInfo.finalTotal.toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}

                <div className="mt-4 border-t border-secondary-100 pt-4 flex items-start gap-2">
                  <i className="ri-information-line text-secondary-400 text-sm mt-0.5 shrink-0" />
                  <p className="text-[11px] text-secondary-500 leading-relaxed">
                    {t('booking.guidePrice')}
                  </p>
                </div>
                <div className="mt-4 flex items-start gap-2">
                  <i className="ri-shield-check-line text-primary-500 text-sm mt-0.5 shrink-0" />
                  <p className="text-[11px] text-secondary-500">
                    {t('booking.noPayment')}
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}